/**
 * DispositionContext - provides per-array-item disposition status to the
 * Field/Section components rendered inside DrugOutputField,
 * ProcedureOutputField and SiteOfCareOutputField.
 *
 * An array output component that renders `Field` MUST wrap its items in
 * ItemDispositionProvider. Field gates its missing-field icon on the context
 * being populated, so a component that omits the provider silently shows no
 * markers at all — while the enclosing accordion's "X/Y captured" badge, which
 * reads dispositionFields directly, still reports the right count.
 *
 * The parent array component (e.g. DrugOutputField) extracts the slice of
 * required/missing fields that belong to a single drug item, strips the
 * `drug[N].` prefix, and supplies:
 *   - sectionScores keyed by sub-section prefix ("coverage", "prior_auth_status", ...)
 *   - fieldStatusMap keyed by item-relative path ("coverage.coinsurance", ...)
 *
 * Section components read sectionScores to render `<SectionScoreBadge>` in
 * their accordion trigger; Field reads fieldStatusMap to render a
 * `<DispositionFieldIcon>` next to its label.
 */

import React, { createContext, useContext, useMemo } from 'react';

import type { DispositionFieldStatus, SectionScore } from '@/utils/dispositionCompleteness';
import {
  buildFieldDispositionMap,
  computeSubsectionScores,
  extractItemDispositionFields,
} from '@/utils/dispositionCompleteness';

interface DispositionContextValue {
  /** "coverage.coinsurance" → 'filled' | 'missing' (item-relative paths) */
  fieldStatusMap: Map<string, DispositionFieldStatus>;
  /** "coverage" → { filled, total } */
  sectionScores: Map<string, SectionScore>;
}

const EMPTY_VALUE: DispositionContextValue = {
  fieldStatusMap: new Map(),
  sectionScores: new Map(),
};

const DispositionContext = createContext<DispositionContextValue>(EMPTY_VALUE);

interface DispositionProviderProps {
  fieldStatusMap: Map<string, DispositionFieldStatus>;
  sectionScores: Map<string, SectionScore>;
  children: React.ReactNode;
}

const DispositionProvider: React.FC<DispositionProviderProps> = ({
  fieldStatusMap,
  sectionScores,
  children,
}) => {
  const value = useMemo(() => ({ fieldStatusMap, sectionScores }), [fieldStatusMap, sectionScores]);
  return <DispositionContext.Provider value={value}>{children}</DispositionContext.Provider>;
};

export const useDispositionContext = (): DispositionContextValue => useContext(DispositionContext);

/**
 * Computes per-item disposition state (field status map + subsection scores)
 * and provides it via DispositionContext. Shared by the array output
 * components to avoid duplicating the same provider logic.
 *
 * A FLAT entity (leaves directly on the item, e.g. sites_of_care) yields
 * dot-less item-relative paths: those populate fieldStatusMap for the per-field
 * icons but produce no subsectionScores, which is correct — there are no
 * subsections to badge.
 */
export const ItemDispositionProvider: React.FC<{
  arrayName: string;
  index: number;
  dispositionFields?: { required_fields?: string[]; missing_fields?: string[] };
  children: React.ReactNode;
}> = ({ arrayName, index, dispositionFields, children }) => {
  const { fieldStatusMap, sectionScores } = useMemo(() => {
    const { required, missing } = extractItemDispositionFields(
      arrayName,
      index,
      dispositionFields?.required_fields,
      dispositionFields?.missing_fields,
    );
    return {
      fieldStatusMap: buildFieldDispositionMap(required, missing),
      sectionScores: computeSubsectionScores(required, missing),
    };
  }, [arrayName, index, dispositionFields?.required_fields, dispositionFields?.missing_fields]);

  return (
    <DispositionProvider fieldStatusMap={fieldStatusMap} sectionScores={sectionScores}>
      {children}
    </DispositionProvider>
  );
};
